import { useEffect, useState } from 'react';
import { serverNow } from './services/worldSocket';

// World time — each district lives on its city's real local time (Hip-Hop
// Town = Oakland, CA). The hour drives an art-directed set of lighting
// keyframes rather than an astronomy model: night → dawn → day → golden →
// dusk → night, with the district's golden-hour palette as the hero look.
//
// Demo override (Demo DoD #5 — a live showing must always look its best):
//   ?time=golden | night | noon | dawn | dusk   or   ?time=18.5 (local hours)

const NAMED_TIMES = { golden: 18.8, night: 23, noon: 13, dawn: 6.3, dusk: 20 };

// Resolve 'dusk' | 'night' | 18.5 → an hour, or null if it means nothing.
export function hourOf(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string' && v in NAMED_TIMES) return NAMED_TIMES[v];
  const n = parseFloat(v);
  return Number.isFinite(n) ? ((n % 24) + 24) % 24 : null;
}

// `pin` pins the clock for a SCENE — a room whose moment is written rather than
// whenever the player happens to arrive. A story beat set at dusk should be at
// dusk. Rooms that leave it unset stay on the live world clock, and that continuity
// is worth keeping wherever the scene does not depend on the hour: the player's
// own evening becomes the scene's evening.
//
// ?time= still wins, because it is the demo override and has to beat everything.
const FORMATTERS = new Map();
export function localHour(timeZone, pin = null) {
  const q = new URLSearchParams(window.location.search).get('time');
  if (q) {
    if (q in NAMED_TIMES) return NAMED_TIMES[q];
    const n = parseFloat(q);
    if (Number.isFinite(n)) return ((n % 24) + 24) % 24;
  }
  const pinned = hourOf(pin);
  if (pinned != null) return pinned;
  // serverNow(): the shared world clock (offset from the /world socket), so every
  // visitor sees the SAME time-of-day/lighting regardless of their device clock.
  // Falls back to local time until the first world_state arrives (offset 0).
  // One formatter per zone, built once: constructing an Intl.DateTimeFormat is
  // the expensive half of this (it loads locale + zone data), and SkyDome,
  // CityBox and the phone all ask for the hour on every render.
  let fmt = FORMATTERS.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, hour: 'numeric', minute: 'numeric' });
    FORMATTERS.set(timeZone, fmt);
  }
  const parts = fmt.formatToParts(new Date(serverNow()));
  const h = Number(parts.find((p) => p.type === 'hour')?.value || 0) % 24;
  const m = Number(parts.find((p) => p.type === 'minute')?.value || 0);
  return h + m / 60;
}

// Lighting keyframes by local hour. `night` is a 0..1 factor the street uses
// to bring up practicals (streetlights, windows); `el` is sun elevation
// (fraction, negative = below horizon).
const STOPS = [
  { h: 0.0,  sky: '#050507', horizon: '#0d1020', fog: '#060609', sun: '#8d9dff', amb: '#3d4a7d', dir: 0.22, ambI: 0.18, el: -0.35, night: 1 },
  { h: 5.0,  sky: '#050507', horizon: '#0d1020', fog: '#060609', sun: '#8d9dff', amb: '#3d4a7d', dir: 0.22, ambI: 0.18, el: -0.3,  night: 1 },
  { h: 6.3,  sky: '#4a3b6b', horizon: '#ff9a8c', fog: '#8c6b8a', sun: '#ffb2a0', amb: '#b39ac0', dir: 0.8,  ambI: 0.3,  el: 0.06,  night: 0.35 },
  { h: 8.0,  sky: '#7db3e8', horizon: '#ffe2b8', fog: '#c9d4e0', sun: '#fff2d8', amb: '#dbe7f4', dir: 1.35, ambI: 0.42, el: 0.45,  night: 0 },
  { h: 13.0, sky: '#8ec4f2', horizon: '#eaf2f8', fog: '#d5e2ec', sun: '#ffffff', amb: '#eef4fa', dir: 1.5,  ambI: 0.5,  el: 0.9,   night: 0 },
  { h: 17.5, sky: '#f0b874', horizon: '#ffd9a0', fog: '#e8b487', sun: '#ffd9a0', amb: '#ffd9a0', dir: 1.5,  ambI: 0.45, el: 0.35,  night: 0 },
  { h: 18.8, sky: '#ff9a3c', horizon: '#ffd27d', fog: '#e8935a', sun: '#ffb25e', amb: '#ffd27d', dir: 1.6,  ambI: 0.4,  el: 0.14,  night: 0.05 },
  { h: 20.0, sky: '#b0567a', horizon: '#ff8a6b', fog: '#7a4a63', sun: '#ff8a6b', amb: '#a06b8a', dir: 0.9,  ambI: 0.3,  el: 0.04,  night: 0.4 },
  { h: 21.2, sky: '#0a081c', horizon: '#241a3c', fog: '#0b0918', sun: '#8d9dff', amb: '#4a5490', dir: 0.3,  ambI: 0.2,  el: -0.2,  night: 1 },
  { h: 24.0, sky: '#050507', horizon: '#0d1020', fog: '#060609', sun: '#8d9dff', amb: '#3d4a7d', dir: 0.22, ambI: 0.18, el: -0.35, night: 1 }
];

const hex = (c) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
const lerp = (a, b, t) => a + (b - a) * t;
function lerpHex(a, b, t) {
  const [r1, g1, b1] = hex(a);
  const [r2, g2, b2] = hex(b);
  return `#${[lerp(r1, r2, t), lerp(g1, g2, t), lerp(b1, b2, t)]
    .map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

export function phaseLabel(h) {
  if (h < 5) return 'night';
  if (h < 7.2) return 'dawn';
  if (h < 11.5) return 'morning';
  if (h < 15) return 'midday';
  if (h < 17.5) return 'afternoon';
  if (h < 19.6) return 'golden hour';
  if (h < 21.2) return 'dusk';
  return 'night';
}

export function timeOfDayAt(h) {
  let i = 0;
  while (i < STOPS.length - 2 && STOPS[i + 1].h <= h) i++;
  const a = STOPS[i];
  const b = STOPS[i + 1];
  const t = Math.min(1, Math.max(0, (h - a.h) / (b.h - a.h || 1)));

  const el = lerp(a.el, b.el, t);
  // Sun arcs east → west across the day, held on the −z side so the light
  // always lands on the road-facing facades.
  const arc = Math.min(1, Math.max(0, (h - 6) / 15));
  const sunX = Math.cos(arc * Math.PI) * 70;

  return {
    hour: h,
    phase: phaseLabel(h),
    night: lerp(a.night, b.night, t),
    sky: lerpHex(a.sky, b.sky, t),
    horizon: lerpHex(a.horizon, b.horizon, t),
    fog: lerpHex(a.fog, b.fog, t),
    sunColor: lerpHex(a.sun, b.sun, t),
    ambColor: lerpHex(a.amb, b.amb, t),
    dirIntensity: lerp(a.dir, b.dir, t),
    ambIntensity: lerp(a.ambI, b.ambI, t),
    sunPos: [sunX, el * 120, -80],
    keyPos: [sunX * 0.6 || 20, Math.max(el * 80, 8), -45]
  };
}

// Live world time for a district — re-evaluates every 30s (a ?time override
// makes it static for demo pinning).
export function useWorldTime(timeZone, pin = null) {
  const [tod, setTod] = useState(() => timeOfDayAt(localHour(timeZone, pin)));
  useEffect(() => {
    const tick = () => setTod(timeOfDayAt(localHour(timeZone, pin)));
    tick();
    // A pinned hour cannot drift, so there is nothing to poll for.
    if (hourOf(pin) != null) return undefined;
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [timeZone, pin]);
  return tod;
}
